import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, Undo2, X } from "lucide-react";

import { cn } from "../../lib/utils.js";
import { useNotifications } from "../../context/NotificationsContext.js";
import { useCloseOnOutsideClick } from "../DropdownButton.js";
import {
  toView, unreadCount, countsFor, badgeText,
  type NotifCategory, type ViewNotification,
} from "./notification-model.js";
import {
  CategoryTile, UnreadDot, FOCUS, EmptyState, CATEGORY_LABEL, NotificationSkeleton,
  useReplayAnimation, rowInClass, rowDelay,
} from "./notification-ui.js";

/** All / Unread, plus one per category that actually has rows. */
type Tab = "all" | "unread" | NotifCategory;

/** Keeps a dismissed row in place long enough to play its collapse. */
const DISMISS_MS = 300;

/**
 * How many rows the panel will render. The store itself holds at most 20, so this
 * is effectively "all of them" and the list scrolls within its max height.
 *
 * It was 5, which is why no scrollbar ever appeared: five rows never exceeded the
 * 360px cap, so the container had nothing to overflow.
 */
const MAX_ROWS = 20;

export function NotificationBell() {
  const navigate = useNavigate();
  const { other, markRead, markUnread, markAllRead, dismiss, clearRead, lastArrival, loading, refresh } = useNotifications();

  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>("all");
  const ref = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLButtonElement>(null);
  useCloseOnOutsideClick(ref, open, () => setOpen(false));

  const [closing, setClosing] = useState<Set<string>>(new Set());

  const items = useMemo(() => other.map(n => toView(n)), [other]);
  const unread = unreadCount(items);
  const counts = useMemo(() => countsFor(items), [items]);   // always unfiltered

  const shown = useMemo(() => {
    const filtered = tab === "all" ? items
      : tab === "unread" ? items.filter(i => !i.read)
      : items.filter(i => i.category === tab);
    return filtered.slice(0, MAX_ROWS);
  }, [items, tab]);

  // Only offer a category chip that has something behind it.
  const tabs: Tab[] = useMemo(() => [
    "all" as const,
    "unread" as const,
    ...(["leave", "attendance", "hours", "other"] as const).filter(c => counts[c] > 0),
  ], [counts]);

  // Entrance replays on open and on every tab change, per the spec.
  const { phase, replay } = useReplayAnimation();
  useEffect(() => { if (open) replay(); }, [open, tab, replay]);

  // Ring + badge pop. Bumping the key remounts the icon, which is the only way to
  // replay a CSS animation already sitting on the element. Fires when the panel is
  // opened and whenever the unread count rises.
  const [ringKey, setRingKey] = useState(0);
  const prevUnread = useRef(unread);
  useEffect(() => {
    if (unread > prevUnread.current) setRingKey(k => k + 1);
    prevUnread.current = unread;
  }, [unread]);

  // Escape closes and returns focus to the bell.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setOpen(false); bellRef.current?.focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  /** Restores the original behaviour: open the thing the notification is about. */
  function openRow(n: ViewNotification) {
    if (!n.read) markRead("other", n.id);
    setOpen(false);
    if (n.link) navigate(n.link);
  }

  function toggleRead(n: ViewNotification) {
    if (n.read) markUnread("other", n.id);
    else markRead("other", n.id);
  }

  function removeRow(id: string) {
    setClosing(prev => new Set(prev).add(id));
    setTimeout(() => {
      dismiss("other", id);
      setClosing(prev => { const next = new Set(prev); next.delete(id); return next; });
    }, DISMISS_MS);
  }

  return (
    // No `relative` here: the header group is the positioning context, shared
    // with the attendance clock so both panels open against the same right edge.
    <div ref={ref}>
      <button
        ref={bellRef}
        type="button"
        onClick={() => {
          // Opening pulls the latest list straight away instead of waiting for the poll.
          if (!open) void refresh();
          setOpen(o => !o);
          setRingKey(k => k + 1);
        }}
        aria-label={`Notifications, ${unread} unread`}
        aria-expanded={open}
        aria-controls="notif-panel"
        className={cn(
          "relative flex h-9 w-9 items-center justify-center rounded-full transition-colors motion-reduce:transition-none",
          FOCUS,
          open ? "text-brand-600" : "text-surface-600 hover:bg-surface-100",
        )}
      >
        <Bell key={ringKey} size={18} className={cn("bell-ring", unread > 0 && !open && "bell-alert")} />
        {unread > 0 && (
          <span
            key={`badge-${ringKey}`}
            aria-hidden="true"
            className="notif-badge-pop absolute -right-1 -top-1 flex h-[19px] min-w-[19px] items-center justify-center rounded-full border-2 border-surface-0 bg-error px-1 text-[11px] font-bold text-white"
          >
            {badgeText(unread)}
          </span>
        )}
      </button>

      {open && (
        <div
          id="notif-panel"
          role="dialog"
          aria-label="Notifications"
          className="notif-panel-in absolute right-0 top-full z-[200] mt-3 w-[420px] overflow-hidden rounded-2xl border border-[var(--color-border)] bg-surface-0 shadow-[0_16px_40px_rgb(16_24_40_/_0.18)]"
        >
          <div className="flex items-center justify-between gap-2 px-4 pb-2 pt-3">
            <span className="text-[15px] font-bold text-surface-900">Notifications</span>
            <span className="flex shrink-0 gap-3">
              {items.some(n => n.read) && (
                <button type="button" onClick={() => clearRead("other")} className={cn("rounded text-[12px] text-surface-500 hover:text-surface-700", FOCUS)}>
                  Clear read
                </button>
              )}
              {unread > 0 && (
                <button type="button" onClick={() => markAllRead("other")} className={cn("rounded text-[12px] font-semibold text-brand-600 hover:underline", FOCUS)}>
                  Mark all read
                </button>
              )}
            </span>
          </div>

          <div className="chip-strip -mx-1 flex gap-1.5 overflow-x-auto px-4 pb-2">
            {tabs.map(t => {
              const count = t === "all" ? counts.all : t === "unread" ? unread : counts[t];
              const label = t === "all" ? "All" : t === "unread" ? "Unread" : CATEGORY_LABEL[t];
              return (
                <button
                  key={t}
                  type="button"
                  aria-pressed={tab === t}
                  onClick={() => setTab(t)}
                  className={cn(
                    "flex h-7 shrink-0 items-center gap-1.5 rounded-full px-2.5 text-[12px] font-semibold transition-colors motion-reduce:transition-none",
                    FOCUS,
                    tab === t ? "bg-brand-600 text-white" : "bg-surface-100 text-surface-600 hover:text-surface-900",
                  )}
                >
                  {label}
                  {count > 0 && (
                    <span className={cn("rounded-full px-1.5 text-[10px]", tab === t ? "bg-white/25" : "bg-surface-200")}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="notif-scroll flex max-h-[360px] flex-col gap-1 overflow-y-auto px-2 pb-2 pt-0.5">
            {loading ? <NotificationSkeleton /> : shown.length === 0 ? <EmptyState compact /> : shown.map((n, i) => (
              // grid wrapper so dismissing can collapse the row's own height.
              <div key={n.id} className={cn("grid grid-rows-[1fr]", closing.has(n.id) && "notif-row-out")}>
                <div className="overflow-hidden">
                  <div
                    style={{ animationDelay: rowDelay(i) }}
                    className={cn(
                      "notif-tint group relative flex items-start gap-2.5 rounded-xl border p-2 pl-3 transition-colors hover:bg-surface-100 focus-within:bg-surface-100 motion-reduce:transition-none",
                      rowInClass(phase),
                      n.read ? "border-transparent" : "border-surface-200 bg-surface-0 shadow-[0_1px_2px_rgb(16_24_40_/_0.04)]",
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        "notif-bar absolute bottom-2 left-0 top-2 w-[3px] rounded-full bg-brand-600",
                        n.read ? "scale-y-0" : "scale-y-100",
                      )}
                    />

                    <CategoryTile category={n.category} className={cn("mt-0.5", n.read ? "opacity-60" : "opacity-100")} />

                    {/* Opens what the notification is about -- the original behaviour. */}
                    <button
                      type="button"
                      onClick={() => openRow(n)}
                      aria-label={`${n.title}. ${n.body}`}
                      className={cn("min-w-0 flex-1 rounded text-left", FOCUS)}
                    >
                      <span className="flex items-baseline gap-2">
                        <span className={cn("notif-tint min-w-0 flex-1 truncate text-[13px]", n.read ? "font-medium text-surface-600" : "font-bold text-surface-900")}>
                          {n.title}
                        </span>
                        <span className="shrink-0 text-[11px] text-surface-400">{n.time}</span>
                      </span>
                      {/* One line: the dropdown is a glance, not the full record. */}
                      <span className="mt-0.5 block truncate text-[12px] leading-snug text-surface-500">{n.body}</span>
                    </button>

                    <span className="flex shrink-0 items-center gap-0.5 pt-0.5">
                      <UnreadDot unread={!n.read} />
                      {/* Hover-only, but also on focus-within for keyboard users. */}
                      <span className="flex gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 motion-reduce:transition-none">
                        <button
                          type="button"
                          onClick={() => toggleRead(n)}
                          aria-label={n.read ? "Mark unread" : "Mark read"}
                          className={cn("rounded p-1 text-surface-400 hover:text-surface-700", FOCUS)}
                        >
                          {n.read ? <Undo2 size={13} /> : <Check size={13} />}
                        </button>
                        <button
                          type="button"
                          onClick={() => removeRow(n.id)}
                          aria-label="Remove from list"
                          className={cn("rounded p-1 text-surface-400 hover:text-surface-700", FOCUS)}
                        >
                          <X size={13} />
                        </button>
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* The spec's footer links to a full notifications page, but this app has
              no /notifications route and the brief says not to add one -- so the
              strip reports what is being withheld instead of offering a dead link. */}
          <div className="border-t border-surface-200 bg-surface-50 px-4 py-2 text-center">
            <span className="text-[11.5px] text-surface-500">
              {loading
                ? "Loading…"
                : items.length > shown.length
                  ? `Showing ${shown.length} of ${items.length}`
                  : `${items.length} notification${items.length === 1 ? "" : "s"}`}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
